import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Chip, ChipProps, CircularProgress, IconButton, Tooltip } from '@mui/material';
import React, { MouseEvent, ReactElement } from 'react';

import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { useGetProcessOfferEmails } from '@/views/processes/offer/components/emails/util/useGetProcessOfferEmails.util.ts';

type IndicatorState = {
  color: ChipProps['color'];
  icon: ReactElement;
  label: string;
  tooltip: string;
  variant: ChipProps['variant'];
};

interface Props {
  iconOnly?: boolean;
  onClick?: () => void;
  processId: string;
  processOfferId: string;
}

export const ProcessOfferEmailsIndicator: React.FC<Props> = ({
  iconOnly,
  onClick,
  processId,
  processOfferId,
}) => {
  const { t } = useTranslationWithPrefix('views.process.offer.emails.indicator');
  const { emails, isPending } = useGetProcessOfferEmails({
    enabled: true,
    processId,
    processOfferId,
  });

  if (isPending) {
    return <CircularProgress size={16} />;
  }

  const state: IndicatorState = ((): IndicatorState => {
    if (emails.length === 0) {
      return {
        color: 'error',
        icon: <HighlightOffIcon />,
        label: t('notInEmail'),
        tooltip: t('tooltipNotAttached'),
        variant: 'outlined',
      };
    }
    if (emails.every(e => e.isDraft)) {
      return {
        color: 'warning',
        icon: <DraftsOutlinedIcon />,
        label: `${t('inEmailDraft')} (${emails.length})`,
        tooltip: t('tooltipDraftOnly'),
        variant: 'filled',
      };
    }
    return {
      color: 'success',
      icon: <CheckCircleOutlineIcon />,
      label: `${t('inEmail')} (${emails.length})`,
      tooltip: t('tooltipAttached'),
      variant: 'filled',
    };
  })();

  const handleClick = onClick
    ? (event: MouseEvent) => {
        event.stopPropagation();
        onClick();
      }
    : undefined;

  if (iconOnly) {
    return (
      <Tooltip placement="left" title={`${state.label} — ${state.tooltip}`}>
        <IconButton color={state.color} onClick={handleClick} size="small">
          {state.icon}
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip placement="left" title={state.tooltip}>
      <Chip
        clickable={Boolean(onClick)}
        color={state.color}
        icon={state.icon}
        label={state.label}
        onClick={handleClick}
        size="small"
        variant={state.variant}
      />
    </Tooltip>
  );
};
